'use client';

import React, { useState } from 'react';
import { Sparkles, FileText, Activity, AlertCircle, TrendingUp, CheckCircle2 } from 'lucide-react';

interface DslmReportProps {
    isDark?: boolean;
}

export default function DslmInsightPanel({ isDark = true }: DslmReportProps) {
    const [projectId, setProjectId] = useState('ID_2531'); // Default to Daegok
    const [loading, setLoading] = useState(false);
    const [report, setReport] = useState<string | null>(null);

    const generateReport = async () => {
        if (!projectId) return;
        setLoading(true);
        try {
            const res = await fetch('/api/report/dslm-insight', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ projectId })
            });
            const data = await res.json();
            setReport(data.report);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={`rounded-2xl border ${isDark ? 'bg-white/[0.02] border-white/[0.08]' : 'bg-white border-gray-200'} overflow-hidden flex flex-col h-full shadow-2xl`}>
            {/* Header */}
            <div className={`px-6 py-4 border-b flex items-center justify-between ${isDark ? 'border-white/[0.08] bg-white/[0.02]' : 'bg-gray-50 border-gray-100'}`}>
                <div className="flex items-center gap-2.5">
                    <div className="p-2 rounded-lg bg-indigo-500/20 text-indigo-400">
                        <Sparkles className="w-4 h-4" />
                    </div>
                    <div>
                        <h3 className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>DSLM 전략 인사이트 리포트</h3>
                        <p className="text-[10px] text-gray-500">NAS + Flow + Cross-Project 통합 분석</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <input 
                        value={projectId} 
                        onChange={e => setProjectId(e.target.value.toUpperCase())}
                        className={`text-[11px] px-3 py-1.5 rounded-lg outline-none border font-mono ${isDark ? 'bg-black/40 border-white/10 text-white' : 'bg-white border-gray-200'}`}
                        placeholder="Project ID..."
                    />
                    <button 
                        onClick={generateReport}
                        disabled={loading}
                        className="px-4 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-[11px] font-bold rounded-lg cursor-pointer hover:shadow-lg disabled:opacity-50"
                    >
                        {loading ? '추론 중...' : '리포트 생성'}
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
                {!report && !loading && (
                    <div className="flex flex-col items-center justify-center h-full text-center text-gray-500">
                        <Activity className="w-12 h-12 mb-4 opacity-10" />
                        <p className="text-sm">프로젝트 ID를 입력하고 리포트 생성 버튼을 누르세요.</p>
                        <p className="text-[11px] mt-2">Gemma-4 Brain이 실시간으로 NAS과 업무 맥락을 전수 조사합니다.</p>
                    </div>
                )}

                {loading && (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                        <div className="relative w-16 h-16 mb-6">
                            <div className="absolute inset-0 border-4 border-indigo-500/20 rounded-full"></div>
                            <div className="absolute inset-0 border-4 border-t-indigo-500 rounded-full animate-spin"></div>
                        </div>
                        <h4 className={`text-sm font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Gemma-4 Brain Reasoning...</h4>
                        <div className="flex gap-2">
                             <span className="text-[11px] text-indigo-400 animate-pulse">NAS 파일 스캔 중...</span>
                             <span className="text-[11px] text-purple-400 animate-pulse" style={{animationDelay: '300ms'}}>Flow 맥락 파싱 중...</span>
                        </div>
                    </div>
                )}

                {report && (
                    <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
                        <div className={`prose prose-sm max-w-none ${isDark ? 'prose-invert' : ''}`}>
                            <div className="space-y-6">
                                {/* Markdown content usually goes here, for simplicity we process the sections */}
                                {report.split('\n\n').map((section, idx) => {
                                    if (section.startsWith('1.') || section.startsWith('2.') || section.startsWith('3.') || section.startsWith('4.')) {
                                        return (
                                            <div key={idx} className={`p-4 rounded-xl border ${isDark ? 'bg-white/[0.03] border-white/10' : 'bg-gray-50 border-gray-100 shadow-sm'}`}>
                                                <h4 className="text-xs font-black uppercase tracking-widest text-indigo-400 mb-3 flex items-center gap-2">
                                                    {section.includes('단계') && <Activity className="w-3.5 h-3.5" />}
                                                    {section.includes('리스크') && <AlertCircle className="w-3.5 h-3.5 text-red-400" />}
                                                    {section.includes('단가') && <TrendingUp className="w-3.5 h-3.5 text-cyan-400" />}
                                                    {section.includes('권고') && <CheckCircle2 className="w-3.5 h-3.5 text-green-400" />}
                                                    {section.split('\n')[0]}
                                                </h4>
                                                <p className={`text-xs leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                                    {section.split('\n').slice(1).join('\n')}
                                                </p>
                                            </div>
                                        );
                                    }
                                    return <p key={idx} className="text-xs text-gray-400 whitespace-pre-wrap">{section}</p>;
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className={`px-6 py-3 border-t text-[10px] text-gray-500 flex justify-between ${isDark ? 'border-white/[0.08]' : 'bg-gray-50'}`}>
                <span>© 2026 SnapGRID DSLM Architecture</span>
                <span className="text-indigo-400 font-bold">ZERO-HALLUCINATION GUARANTEED</span>
            </div>
        </div>
    );
}
